import {
  MOCK_APPS,
  MOCK_DEVICES,
  MOCK_FLEET_SETTINGS,
  MOCK_OPERATOR,
  MOCK_EXTRACTIONS,
  MOCK_RUNS,
  MOCK_TASKS,
  emptyTaskRow,
  mockDeviceDetail,
  mockOverview,
  mockSteps,
} from "./data";
import type { DeviceSummary, RunRow, TaskRow } from "@/types/fleet";

export const MOCKS_ENABLED = import.meta.env.VITE_USE_MOCKS === "1";

/** Network feels instant in mocks otherwise, and skeletons never get exercised. */
const LATENCY_MS = 180;

const tasks: TaskRow[] = MOCK_TASKS.map((t) => ({ ...t }));
const devices: DeviceSummary[] = MOCK_DEVICES.map((d) => ({ ...d }));
const runs: RunRow[] = MOCK_RUNS.map((r) => ({ ...r }));

function delay<T>(value: T): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), LATENCY_MS));
}

function json(body: unknown, status = 200): Response {
  return new Response(JSON.stringify(body), {
    status,
    headers: { "Content-Type": "application/json" },
  });
}

function apiError(status: number, code: string, message: string): Response {
  return json({ error: { code, message, details: {} } }, status);
}

/** Keyset pagination over an array, using the offset as an opaque cursor. */
function page<T>(items: T[], url: URL) {
  const limit = Number(url.searchParams.get("limit") ?? 50);
  const cursor = Number(url.searchParams.get("cursor") ?? 0);
  const slice = items.slice(cursor, cursor + limit);
  const next = cursor + limit < items.length ? String(cursor + limit) : null;
  return { items: slice, next_cursor: next };
}

/**
 * Route table for the mock server.
 *
 * Every entry mirrors a row of `protocol/rest-api.md`. When the real server
 * lands, the diff between these handlers and its behaviour is the list of things
 * to check — which is worth more than the fixtures themselves.
 */
function handle(method: string, url: URL, body: unknown): Response | null {
  const p = url.pathname.replace(/^\/api\/v1/, "");

  /* ---- auth ---- */
  if (p === "/auth/login" && method === "POST") return json({ operator: MOCK_OPERATOR });
  if (p === "/auth/logout" && method === "POST") return new Response(null, { status: 204 });
  if (p === "/auth/me") return json(MOCK_OPERATOR);

  /* ---- overview ---- */
  if (p === "/overview") return json(mockOverview());

  /* ---- devices ---- */
  if (p === "/devices" && method === "GET") {
    let list = devices;
    const q = url.searchParams.get("q")?.toLowerCase();
    const health = url.searchParams.get("health");
    const tag = url.searchParams.get("tag");
    const hasRun = url.searchParams.get("has_run");
    if (q) list = list.filter((d) => d.label.toLowerCase().includes(q) || d.id.toLowerCase().includes(q));
    if (health) list = list.filter((d) => d.health === health);
    if (tag) list = list.filter((d) => d.tags.includes(tag));
    if (hasRun !== null) list = list.filter((d) => (hasRun === "1" ? !!d.active_run : !d.active_run));

    const sort = url.searchParams.get("sort");
    if (sort) {
      const desc = sort.startsWith("-");
      const key = (desc ? sort.slice(1) : sort) as keyof DeviceSummary;
      list = [...list].sort((a, b) => {
        const av = a[key] ?? "";
        const bv = b[key] ?? "";
        const cmp = av < bv ? -1 : av > bv ? 1 : 0;
        return desc ? -cmp : cmp;
      });
    }
    return json(page(list, url));
  }

  const deviceMatch = /^\/devices\/([^/]+)(\/.*)?$/.exec(p);
  if (deviceMatch) {
    const [, id, rest] = deviceMatch;
    if (!rest && method === "GET") {
      const d = mockDeviceDetail(id);
      return d ? json(d) : apiError(404, "not_found", "device not found");
    }
    if (!rest && method === "PATCH") {
      const idx = devices.findIndex((d) => d.id === id);
      if (idx < 0) return apiError(404, "not_found", "device not found");
      const patchBody = (body ?? {}) as Partial<DeviceSummary>;
      devices[idx] = { ...devices[idx], ...patchBody };
      return json({ ...mockDeviceDetail(id), ...patchBody });
    }
    if (rest === "/apps") {
      const d = devices.find((x) => x.id === id);
      if (!d?.online) return apiError(504, "device_offline", "device did not respond");
      return json({ apps: MOCK_APPS });
    }
    if (rest?.startsWith("/logs")) {
      const d = devices.find((x) => x.id === id);
      if (!d?.online) return apiError(504, "device_offline", "device is offline");
      return json({
        lines: Array.from(
          { length: 40 },
          (_, i) =>
            `2026-09-08 14:${String(20 + Math.floor(i / 3)).padStart(2, "0")}:${String((i * 7) % 60).padStart(2, "0")} I/Agent  step ${i + 1} ok latency=${700 + i * 13}ms`,
        ),
      });
    }
  }

  /* ---- tasks ---- */
  if (p === "/tasks" && method === "GET") return json({ items: tasks, next_cursor: null });
  if (p === "/tasks" && method === "POST") {
    const spec = body as TaskRow;
    const created = { ...emptyTaskRow(`tsk_${Date.now().toString(36)}`), ...spec };
    created.id = created.id || `tsk_${Date.now().toString(36)}`;
    tasks.push(created);
    return json(created);
  }
  if (p === "/tasks/validate-schema" && method === "POST") {
    const raw = (body as { schema?: string })?.schema ?? "";
    try {
      const parsed = JSON.parse(raw) as { properties?: Record<string, { type?: string }>; required?: string[] };
      const required = new Set(parsed.required ?? []);
      const columns = Object.entries(parsed.properties ?? {}).map(([name, v]) => ({
        name,
        type: v?.type ?? "any",
        required: required.has(name),
      }));
      return json({ ok: true, errors: [], columns });
    } catch (e) {
      return json({
        ok: false,
        errors: [{ path: "/", message: (e as Error).message }],
        columns: [],
      });
    }
  }

  const taskMatch = /^\/tasks\/([^/]+)$/.exec(p);
  if (taskMatch) {
    const id = taskMatch[1];
    const idx = tasks.findIndex((t) => t.id === id);
    if (method === "GET") return idx >= 0 ? json(tasks[idx]) : apiError(404, "not_found", "task not found");
    if (method === "PUT") {
      if (idx < 0) return apiError(404, "not_found", "task not found");
      tasks[idx] = { ...tasks[idx], ...(body as TaskRow), updated_at: new Date().toISOString() };
      return json(tasks[idx]);
    }
    if (method === "DELETE") {
      if (idx >= 0) tasks.splice(idx, 1);
      return new Response(null, { status: 204 });
    }
  }

  /* ---- runs ---- */
  if (p === "/runs" && method === "GET") {
    let list = runs;
    const deviceId = url.searchParams.get("device_id");
    const taskId = url.searchParams.get("task_id");
    const status = url.searchParams.get("status");
    const hasError = url.searchParams.get("has_error");
    const since = url.searchParams.get("since");
    if (deviceId) list = list.filter((r) => r.device.id === deviceId);
    if (taskId) list = list.filter((r) => r.taskId === taskId);
    if (status) list = list.filter((r) => r.status === status);
    if (hasError === "true" || hasError === "1") list = list.filter((r) => !!r.error);
    if (since) {
      const t = new Date(since).getTime();
      list = list.filter((r) => (r.startedAt ?? r.createdAt) >= t);
    }
    return json(page(list, url));
  }

  const runMatch = /^\/runs\/([^/]+)(\/.*)?$/.exec(p);
  if (runMatch) {
    const [, id, rest] = runMatch;
    const r = runs.find((x) => x.id === id);
    if (!rest && method === "GET") {
      return r
        ? json({ ...r, extraction_count: r.extractions })
        : apiError(404, "not_found", "run not found");
    }
    if (rest === "/steps") {
      if (!r) return apiError(404, "not_found", "run not found");
      return json({ items: mockSteps(r.id, Math.max(r.steps, 1)), next_cursor: null });
    }
    if (rest === "/stop" && method === "POST") {
      if (!r) return apiError(404, "not_found", "run not found");
      r.status = "CANCELLED";
      r.finishedAt = Date.now();
      return json({ stopped: true });
    }
  }

  if (p === "/tasks/draft" && method === "POST") {
    const prompt = String((body as { prompt?: string })?.prompt ?? "");
    return json({
      spec: {
        ...MOCK_TASKS[0],
        id: "drafted",
        name: prompt.slice(0, 20) || "草稿任務",
        goal: `打開 App，${prompt}`,
      },
      notes: "（模擬）假設你要嘅係香港。",
      variables: [],
      declared_variables: [],
      errors: [],
    });
  }

  /* ---- extractions ---- */
  if (p === "/extractions" && method === "GET") {
    let list = MOCK_EXTRACTIONS;
    const runId = url.searchParams.get("run_id");
    const taskId = url.searchParams.get("task_id");
    const deviceId = url.searchParams.get("device_id");
    const q = url.searchParams.get("q");
    if (runId) list = list.filter((e) => e.run_id === runId);
    if (taskId) list = list.filter((e) => e.task_id === taskId);
    if (deviceId) list = list.filter((e) => e.device_id === deviceId);
    if (q) {
      const needle = q.toLowerCase();
      list = list.filter((e) => JSON.stringify(e.data).toLowerCase().includes(needle));
    }
    return json(page(list, url));
  }

  /* ---- dispatch ---- */
  if (p === "/dispatch/preview" && method === "POST") {
    const target = (body as { target?: { device_ids?: string[] } })?.target;
    const ids = target?.device_ids ?? [];
    const chosen = devices.filter((d) => ids.includes(d.id));
    const online = chosen.filter((d) => d.health === "online").length;
    const stale = chosen.filter((d) => d.health === "stale").length;
    const offline = chosen.filter((d) => d.health === "offline" || d.health === "unseen").length;
    const skipped = chosen
      .filter((d) => d.control.enabled || d.disarmed)
      .map((d) => ({
        device_id: d.id,
        reason: d.control.enabled ? "device_under_manual_control" : "device_disarmed",
      }));
    const staggerMs = (body as { stagger_ms?: number })?.stagger_ms ?? 0;
    return json({
      affected: chosen.length - skipped.length,
      breakdown: { online, stale, offline },
      skipped,
      estimated_seconds: Math.round((chosen.length * staggerMs) / 1000) || chosen.length * 3,
    });
  }

  if (p === "/dispatch" && method === "POST") {
    const target = (body as { target?: { device_ids?: string[] } })?.target;
    const ids = target?.device_ids ?? [];
    return json(
      {
        dispatch_id: "dsp_01JMOCKDISPATCH",
        affected: ids.length,
        breakdown: { online: ids.length, stale: 0, offline: 0 },
        skipped: [],
        estimated_seconds: ids.length * 3,
      },
      202,
    );
  }

  const dispatchMatch = /^\/dispatch\/([^/]+)(\/.*)?$/.exec(p);
  if (dispatchMatch) {
    const [, id, rest] = dispatchMatch;
    if (rest === "/cancel") return json({ cancelled: true });
    return json({
      id,
      task_name: tasks[0]?.name ?? "任務",
      counts: { queued: 2, sent: 3, running: 4, success: 6, failed: 1, expired: 0, skipped: 0 },
      devices: devices.slice(0, 16).map((d, i) => ({
        device_id: d.id,
        command_id: `cmd_${i}`,
        state: (["success", "running", "sent", "queued", "failed"] as const)[i % 5],
        run_id: i % 5 === 0 ? runs[i % runs.length].id : null,
      })),
    });
  }

  /* ---- settings ---- */
  if (p === "/settings/fleet") return json(MOCK_FLEET_SETTINGS);
  if (p === "/groups") return json({ items: [], next_cursor: null });

  return null;
}

/**
 * Patch `fetch` so every `/api/v1` call is answered locally.
 *
 * A fetch patch rather than a service worker on purpose: it needs no extra
 * dependency, no generated file to keep in the repo, and it cannot survive into
 * a production build — `MOCKS_ENABLED` is a compile-time constant, so the whole
 * module tree-shakes away when the flag is off.
 */
export function installMocks() {
  const original = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
    const href =
      typeof input === "string" ? input : input instanceof URL ? input.href : input.url;
    const url = new URL(href, window.location.origin);

    if (!url.pathname.startsWith("/api/v1")) return original(input, init);

    const method = (init?.method ?? "GET").toUpperCase();
    let body: unknown;
    if (typeof init?.body === "string") {
      try {
        body = JSON.parse(init.body);
      } catch {
        body = init.body;
      }
    }

    const res = handle(method, url, body);
    if (res) return delay(res);
    return delay(apiError(404, "not_found", `mock: no handler for ${method} ${url.pathname}`));
  };

  console.info("[mocks] InfoCollect Fleet mock API installed (VITE_USE_MOCKS=1)");
}
